import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, ArrowRight, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { VUKZ } from '../theme/vukzTheme';
import OnboardingAssistant from '../components/onboarding/OnboardingAssistant';
import { ActionType } from '../components/onboarding/agentActions';
import { useSportsLang } from '../contexts/SportsLangContext';
import { ENABLED_SPORTS } from '../services/onboardingService';

/**
 * RegisterPageV2 — B2B Club Registration (VUKZ)
 *
 * Single registration path: every public signup creates a club admin account.
 * 2-step flow:
 *   Step 1 — Dados do responsável (name, email, password, sport)
 *   Step 2 — Termos e consentimentos (LGPD)
 *
 * Backend receives intent='club' + userType='club_admin'.
 * On success: JWT returned immediately → redirect to /manager/setup (onboarding wizard).
 */

const V = {
  bg: '#fafafa',
  text: '#111111',
  textSoft: '#555555',
  textMute: '#8a8a8a',
  accent: '#111111',
  border: '#e5e7eb',
  cardBg: '#ffffff',
  tagBg: '#f3f3f3',
  error: '#dc2626',
  errorBg: '#fef2f2',
  errorBorder: '#fecaca',
};

// Esportes: fonte única em onboardingService.SPORT_OPTIONS (flag `enabled`).
// Mesma lista do wizard — destravar um esporte lá repovoa aqui automaticamente.

const RegisterPageV2 = () => {
  const navigate = useNavigate();
  const { register, loading } = useAuth();
  const { lang: LANG } = useSportsLang();
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  // Registration mode: 'gestor' (default) or 'coach' (coach-as-manager fallback)
  const [registrationMode, setRegistrationMode] = useState('gestor');
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    primary_sport: '',
    organization_name: '',
    phone: '',
    country: 'Brasil',
    city: '',
    acceptTerms: false,
    research_consent: false,
    marketing_consent: false,
  });

  // Lang from sidebar toggle (SportsLangContext already initialized by useSportsLang above)
  // LANG is already set via const { lang: LANG } = useSportsLang() above
  const T = {
    en: {
      mainHeading: 'Register your club on VUKZ',
      alreadyHaveAccount: 'Already have an account?',
      signInHere: 'Sign in here',
      stepLabels: ['Details', 'Terms'],
      bannerTitle: 'Club registration',
      bannerDesc: 'Create your account as manager and then set up your club on the platform.',
      step1Heading: 'Manager details',
      step1Sub: 'Create your account to manage your club or team',
      roleLabel: 'What is your role?',
      gestorTitle: 'Manager / Director',
      gestorDesc: 'I manage the club or organization',
      coachTitle: 'Coach',
      coachDesc: "I'm the technical lead",
      fullNameLabel: 'Full name',
      fullNamePlaceholder: 'Your name',
      emailPlaceholder: 'email@yourclub.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Minimum 6 characters',
      confirmPasswordLabel: 'Confirm password',
      confirmPasswordPlaceholder: 'Repeat password',
      clubNameLabel: 'Club name (optional)',
      clubNamePlaceholder: 'Ex: FC Barcelona, Real Madrid...',
      phoneLabel: 'Phone (optional)',
      countryLabel: 'Country',
      cityLabel: 'City',
      sportLabel: "Club's main sport",
      continueBtn: 'Continue',
      step2Heading: 'Terms and consents',
      step2Sub: 'Read and accept the terms to activate your club',
      acceptTermsText: 'I accept the',
      termsLink: 'terms of use',
      andText: 'and',
      privacyLink: 'privacy policy',
      researchConsentTitle: 'I authorize the use of anonymized data for research (optional)',
      researchConsentDesc: 'Aggregated and anonymized data may be used in academic studies. No personal information is shared.',
      marketingConsentTitle: 'Receive news and updates from VUKZ (optional)',
      marketingConsentDesc: 'We will send emails with platform news, features, and events.',
      backBtn: 'Back',
      loadingBtn: 'Creating account...',
      submitBtn: 'Register club',
      lgpd: '🔒 Your data is protected under LGPD',
      sports: { football: 'Football', swimming: 'Swimming', basketball: 'Basketball', volleyball: 'Volleyball', athletics: 'Athletics', judo: 'Judo', other: 'Other' },
      errFullName: 'Full name is required',
      errEmail: 'Valid email is required',
      errPassword: 'Password must be at least 6 characters',
      errConfirmPassword: 'Passwords do not match',
      errSport: "Select the club's main sport",
      errTerms: 'You must accept the terms of use',
      errGeneral: 'Error creating account. Please try again.',
      errUnexpected: 'Unexpected error. Please try again.',
    },
    pt: {
      mainHeading: 'Cadastre seu clube na VUKZ',
      alreadyHaveAccount: 'Já tem uma conta?',
      signInHere: 'Entre aqui',
      stepLabels: ['Dados', 'Termos'],
      bannerTitle: 'Cadastro do clube',
      bannerDesc: 'Crie sua conta como gestor e em seguida configure seu clube na plataforma.',
      step1Heading: 'Dados do responsável',
      step1Sub: 'Crie sua conta para gerenciar seu clube ou equipe',
      roleLabel: 'Qual é o seu papel?',
      gestorTitle: 'Gestor / Diretor',
      gestorDesc: 'Gerencio o clube ou organização',
      coachTitle: 'Treinador',
      coachDesc: 'Sou o responsável técnico',
      fullNameLabel: 'Nome completo',
      fullNamePlaceholder: 'Seu nome',
      emailPlaceholder: 'email@seuclube.com.br',
      passwordLabel: 'Senha',
      passwordPlaceholder: 'Mínimo 6 caracteres',
      confirmPasswordLabel: 'Confirmar senha',
      confirmPasswordPlaceholder: 'Repita a senha',
      clubNameLabel: 'Nome do clube (opcional)',
      clubNamePlaceholder: 'Ex: Flamengo, Minas Tênis Clube...',
      phoneLabel: 'Telefone (opcional)',
      countryLabel: 'País',
      cityLabel: 'Cidade',
      sportLabel: 'Esporte principal do clube',
      continueBtn: 'Continuar',
      step2Heading: 'Termos e consentimentos',
      step2Sub: 'Leia e aceite os termos para ativar seu clube',
      acceptTermsText: 'Eu aceito os',
      termsLink: 'termos de uso',
      andText: 'e',
      privacyLink: 'política de privacidade',
      researchConsentTitle: 'Autorizo uso de dados anonimizados para pesquisa (opcional)',
      researchConsentDesc: 'Dados agregados e anonimizados podem ser usados em estudos acadêmicos. Nenhuma informação pessoal é compartilhada.',
      marketingConsentTitle: 'Receber novidades e atualizações da VUKZ (opcional)',
      marketingConsentDesc: 'Enviaremos e-mails com novidades, recursos e eventos da plataforma.',
      backBtn: 'Voltar',
      loadingBtn: 'Criando conta...',
      submitBtn: 'Cadastrar clube',
      lgpd: '🔒 Seus dados estão protegidos de acordo com a LGPD',
      sports: { football: 'Futebol', swimming: 'Natação', basketball: 'Basquete', volleyball: 'Vôlei', athletics: 'Atletismo', judo: 'Judô', other: 'Outro' },
      errFullName: 'Nome completo é obrigatório',
      errEmail: 'Email válido é obrigatório',
      errPassword: 'Senha deve ter pelo menos 6 caracteres',
      errConfirmPassword: 'Senhas não coincidem',
      errSport: 'Selecione o esporte principal do clube',
      errTerms: 'Você deve aceitar os termos de uso',
      errGeneral: 'Erro ao criar conta. Tente novamente.',
      errUnexpected: 'Erro inesperado. Tente novamente.',
    },
  }[LANG];

  /* ── Agent action handler ── */
  const handleAgentAction = useCallback((action) => {
    if (action.type === ActionType.FILL_FIELDS && action.fields) {
      setFormData(prev => ({ ...prev, ...action.fields }));
    }
    if (action.type === ActionType.SELECT_SPORT && action.sportId) {
      setFormData(prev => ({ ...prev, primary_sport: action.sportId }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /* ── Validation ── */
  const validateStep1 = () => {
    const e = {};
    if (!formData.full_name || formData.full_name.trim().length < 2)
      e.full_name = T.errFullName;
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))
      e.email = T.errEmail;
    if (!formData.password || formData.password.length < 6)
      e.password = T.errPassword;
    if (formData.password !== formData.confirmPassword)
      e.confirmPassword = T.errConfirmPassword;
    if (!formData.primary_sport)
      e.primary_sport = T.errSport;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!formData.acceptTerms)
      e.acceptTerms = T.errTerms;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setErrors({});
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    try {
      const registrationData = {
        name: formData.full_name,
        email: formData.email,
        password: formData.password,

        // B2B registration — gestor or coach-as-manager
        intent: 'club',
        userType: registrationMode === 'coach' ? 'coach_manager' : 'club_admin',

        // Directory fields
        primaryRole: registrationMode === 'coach' ? 'coaching' : 'management',
        roleSubtype: '',
        primarySport: formData.primary_sport,
        directoryVisibility: 'public',
        claimIntent: false,

        // Organization
        organizationName: formData.organization_name,
        organizationType: 'club',

        // Profile
        phone: formData.phone,
        country: formData.country,
        city: formData.city,

        // Consents
        acceptTerms: formData.acceptTerms,
        researchConsent: formData.research_consent,
        marketingConsent: formData.marketing_consent,
        dataSharingConsent: false,
      };

      const result = await register(registrationData);

      if (result.success) {
        navigate('/manager/setup');
      } else {
        setErrors({
          general: result.error || T.errGeneral,
        });
      }
    } catch (error) {
      console.error('[Register] Exceção:', error);
      setErrors({ general: T.errUnexpected });
    }
  };

  /* ── Field helper — plain function, NOT a React component ── */
  const renderField = (label, name, { type = 'text', placeholder = '', required = false } = {}) => (
    <div>
      <label htmlFor={name} className="block text-sm font-semibold mb-1.5" style={{ color: V.text }}>
        {label} {required && <span style={{ color: V.error }}>*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type === 'password' && showPassword ? 'text' : type}
        value={formData[name]}
        onChange={handleChange}
        onFocus={() => setFocusedField(name)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
        style={{
          borderColor: errors[name] ? V.error : V.border,
          backgroundColor: V.cardBg,
        }}
      />
      {errors[name] && <p className="mt-1 text-xs" style={{ color: V.error }}>{errors[name]}</p>}
    </div>
  );

  /* ══════════════════════════════════════════════════════
     STEP 1 — Dados do responsável
     ══════════════════════════════════════════════════════ */
  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold" style={{ color: V.text }}>{T.step1Heading}</h2>
        <p className="text-sm mt-1" style={{ color: V.textSoft }}>
          {T.step1Sub}
        </p>
      </div>

      {/* Registration mode picker: Gestor vs Coach */}
      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: V.text }}>
          {T.roleLabel}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setRegistrationMode('gestor')}
            className="flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 text-center transition-all hover:shadow-sm"
            style={{
              borderColor: registrationMode === 'gestor' ? V.accent : V.border,
              backgroundColor: registrationMode === 'gestor' ? V.tagBg : V.cardBg,
            }}
          >
            <span className="text-xl">🏢</span>
            <span className="text-sm font-bold" style={{ color: V.text }}>{T.gestorTitle}</span>
            <span className="text-xs" style={{ color: V.textSoft }}>{T.gestorDesc}</span>
          </button>
          <button
            type="button"
            onClick={() => setRegistrationMode('coach')}
            className="flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 text-center transition-all hover:shadow-sm"
            style={{
              borderColor: registrationMode === 'coach' ? V.accent : V.border,
              backgroundColor: registrationMode === 'coach' ? V.tagBg : V.cardBg,
            }}
          >
            <span className="text-xl">🏊‍♂️</span>
            <span className="text-sm font-bold" style={{ color: V.text }}>{T.coachTitle}</span>
            <span className="text-xs" style={{ color: V.textSoft }}>{T.coachDesc}</span>
          </button>
        </div>
      </div>

      {errors.general && (
        <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: V.errorBg, border: `1px solid ${V.errorBorder}`, color: V.error }}>
          {errors.general}
        </div>
      )}

      {renderField(T.fullNameLabel, 'full_name', { placeholder: T.fullNamePlaceholder, required: true })}
      {renderField('Email', 'email', { type: 'email', placeholder: T.emailPlaceholder, required: true })}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="relative">
          {renderField(T.passwordLabel, 'password', { type: 'password', placeholder: T.passwordPlaceholder, required: true })}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[34px] text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {renderField(T.confirmPasswordLabel, 'confirmPassword', { type: 'password', placeholder: T.confirmPasswordPlaceholder, required: true })}
      </div>

      {renderField(T.clubNameLabel, 'organization_name', { placeholder: T.clubNamePlaceholder })}
      {renderField(T.phoneLabel, 'phone', { type: 'tel', placeholder: '(11) 99999-0000' })}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {renderField(T.countryLabel, 'country', { placeholder: 'Brasil' })}
        {renderField(T.cityLabel, 'city', { placeholder: 'São Paulo' })}
      </div>

      {/* Sport picker */}
      <div>
        <label className="block text-sm font-semibold mb-2" style={{ color: V.text }}>
          {T.sportLabel} <span style={{ color: V.error }}>*</span>
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {ENABLED_SPORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, primary_sport: s.value }));
                if (errors.primary_sport) setErrors(prev => ({ ...prev, primary_sport: '' }));
              }}
              className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 text-center transition-all hover:shadow-sm"
              style={{
                borderColor: formData.primary_sport === s.value ? V.accent : V.border,
                backgroundColor: formData.primary_sport === s.value ? V.tagBg : V.cardBg,
              }}
            >
              <span className="text-xs font-semibold" style={{ color: V.text }}>{T.sports[s.value] || s.label}</span>
            </button>
          ))}
        </div>
        {errors.primary_sport && <p className="mt-1 text-xs" style={{ color: V.error }}>{errors.primary_sport}</p>}
      </div>

      <button
        type="button"
        onClick={handleNext}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90 disabled:opacity-50"
        style={{ backgroundColor: V.accent }}
      >
        {T.continueBtn} <ArrowRight size={18} />
      </button>
    </div>
  );

  /* ══════════════════════════════════════════════════════
     STEP 2 — Termos & Consentimentos
     ══════════════════════════════════════════════════════ */
  const renderStep2 = () => (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold" style={{ color: V.text }}>{T.step2Heading}</h2>
        <p className="text-sm mt-1" style={{ color: V.textSoft }}>
          {T.step2Sub}
        </p>
      </div>

      {errors.general && (
        <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: V.errorBg, border: `1px solid ${V.errorBorder}`, color: V.error }}>
          {errors.general}
        </div>
      )}

      {/* Terms */}
      <div className="p-4 rounded-xl border" style={{ borderColor: errors.acceptTerms ? V.error : V.border }}>
        <div className="flex items-start gap-3">
          <input
            id="acceptTerms"
            name="acceptTerms"
            type="checkbox"
            checked={formData.acceptTerms}
            onChange={handleChange}
            className="mt-1 h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="acceptTerms" className="text-sm" style={{ color: V.text }}>
            <span className="font-semibold">{T.acceptTermsText}</span>{' '}
            <Link to="/termos" className="underline font-semibold" style={{ color: V.accent }}>{T.termsLink}</Link>{' '}
            {T.andText}{' '}
            <Link to="/privacidade" className="underline font-semibold" style={{ color: V.accent }}>{T.privacyLink}</Link>
            <span style={{ color: V.error }}> *</span>
          </label>
        </div>
        {errors.acceptTerms && <p className="mt-2 text-xs ml-7" style={{ color: V.error }}>{errors.acceptTerms}</p>}
      </div>

      {/* Research consent */}
      <div className="p-4 rounded-xl border" style={{ borderColor: V.border }}>
        <div className="flex items-start gap-3">
          <input
            id="research_consent"
            name="research_consent"
            type="checkbox"
            checked={formData.research_consent}
            onChange={handleChange}
            className="mt-1 h-4 w-4 rounded border-gray-300"
          />
          <div>
            <label htmlFor="research_consent" className="text-sm font-semibold" style={{ color: V.text }}>
              {T.researchConsentTitle}
            </label>
            <p className="text-xs mt-1" style={{ color: V.textMute }}>
              {T.researchConsentDesc}
            </p>
          </div>
        </div>
      </div>

      {/* Marketing consent */}
      <div className="p-4 rounded-xl border" style={{ borderColor: V.border }}>
        <div className="flex items-start gap-3">
          <input
            id="marketing_consent"
            name="marketing_consent"
            type="checkbox"
            checked={formData.marketing_consent}
            onChange={handleChange}
            className="mt-1 h-4 w-4 rounded border-gray-300"
          />
          <div>
            <label htmlFor="marketing_consent" className="text-sm font-semibold" style={{ color: V.text }}>
              {T.marketingConsentTitle}
            </label>
            <p className="text-xs mt-1" style={{ color: V.textMute }}>
              {T.marketingConsentDesc}
            </p>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 font-bold text-sm transition-opacity hover:opacity-80"
          style={{ borderColor: V.border, color: V.textSoft }}
        >
          <ArrowLeft size={16} /> {T.backBtn}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: V.accent }}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {T.loadingBtn}
            </>
          ) : (
            <>{T.submitBtn} <ArrowRight size={18} /></>
          )}
        </button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════
     PAGE SHELL
     ══════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: V.bg }}>
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black" style={{ color: V.text }}>
            {T.mainHeading}
          </h1>
          <p className="mt-2 text-sm" style={{ color: V.textSoft }}>
            {T.alreadyHaveAccount}{' '}
            <Link to="/login" className="font-semibold underline" style={{ color: V.accent }}>
              {T.signInHere}
            </Link>
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-3 mb-8">
          {[1, 2].map((n) => (
            <div key={n} className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors"
                style={{
                  backgroundColor: step >= n ? V.accent : V.tagBg,
                  color: step >= n ? '#ffffff' : V.textMute,
                }}
              >
                {n}
              </div>
              {n < 2 && (
                <div
                  className="w-12 h-1 rounded"
                  style={{ backgroundColor: step > n ? V.accent : V.tagBg }}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between max-w-[200px] mx-auto mb-6">
          <span className="text-xs font-medium" style={{ color: step >= 1 ? V.text : V.textMute }}>{T.stepLabels[0]}</span>
          <span className="text-xs font-medium" style={{ color: step >= 2 ? V.text : V.textMute }}>{T.stepLabels[1]}</span>
        </div>

        {/* Club intent banner */}
        <div className="mb-6 p-4 rounded-xl border-2 flex items-center gap-4" style={{ borderColor: V.accent, backgroundColor: V.cardBg }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: V.accent }}>
            <Building2 size={20} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: V.text }}>{T.bannerTitle}</p>
            <p className="text-xs" style={{ color: V.textMute }}>
              {T.bannerDesc}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="rounded-2xl border p-6 sm:p-8" style={{ backgroundColor: V.cardBg, borderColor: V.border }}>
          <form onSubmit={handleSubmit}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
          </form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs" style={{ color: V.textMute }}>
            {T.lgpd}
          </p>
        </div>
      </div>

      {/* Onboarding Assistant */}
      <OnboardingAssistant
        context="register"
        step={step}
        focusedField={focusedField}
        errors={errors}
        formData={formData}
        onApplyAction={handleAgentAction}
      />
    </div>
  );
};

export default RegisterPageV2;
